import { usePlateSelectors } from "@udecode/plate-core";
import { forwardRef, useCallback, useImperativeHandle } from "react";

import { TextEditor } from "../textEditor/TextEditor";
import { resetEditor } from "../textEditor/helpers";

import type { NoteColor } from "../noteCard/NoteCard";
import type {
  TextEditorChangeEvent,
  TextEditorSaveEvent,
} from "../textEditor/TextEditor";
import type {
  NoteEditorModalChangeEvent,
  NoteEditorModalSaveEvent,
} from "./NoteEditorModal";

export interface NoteEditorModalBodyProps {
  useSimpleEditor?: boolean;
  autoSave?: boolean;
  color: NoteColor;
  id?: string;
  initialValue?: string;
  limit?: number;
  resetAfterSave?: boolean;
  onChange?: (event: NoteEditorModalChangeEvent) => void;
  onSave?: (event: NoteEditorModalSaveEvent) => Promise<void>;
  onDraftSave?: (event: NoteEditorModalSaveEvent) => Promise<void>;
}

export interface NoteEditorModalBodyRef {
  focus: () => void;
}

export const NoteEditorModalBody = forwardRef<
  NoteEditorModalBodyRef,
  NoteEditorModalBodyProps
>(function NoteEditorModalBody(
  {
    useSimpleEditor = false,
    autoSave,
    color,
    id,
    initialValue,
    limit,
    resetAfterSave,
    onChange,
    onSave,
    onDraftSave,
  },
  ref,
) {
  const editor = usePlateSelectors().editor();

  useImperativeHandle(ref, () => ({
    focus,
  }));

  const handleChange = useCallback(
    (event: TextEditorChangeEvent) => {
      onChange?.({ value: event.value, color });
    },
    [color, onChange],
  );

  const handleSave = useCallback(
    async (event: TextEditorSaveEvent) => {
      await onSave?.({ value: event.value, color });

      if (editor && resetAfterSave) {
        resetEditor(editor);
      }
    },
    [color, editor, onSave, resetAfterSave],
  );

  const handleDraftSave = useCallback(
    async (event: TextEditorSaveEvent) => {
      await onDraftSave?.({ value: event.value, color });
    },
    [color, onDraftSave],
  );

  return (
    <div className="NoteEditorModalBody">
      <TextEditor
        useSimpleEditor={useSimpleEditor}
        autoFocus
        autoSave={autoSave}
        id={id}
        initialValue={initialValue}
        limit={limit}
        onChange={handleChange}
        onSave={handleSave}
        onDraftSave={handleDraftSave}
      />
    </div>
  );
});
